import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  uploadLegalPolicy, 
  searchLegalPolicies, 
  getLegalPolicySources, 
  deleteLegalPolicy, 
  deleteLegalPoliciesBulk, 
  deleteLegalPoliciesBySource 
} from '../services/api';
import FileUpload from '../components/FileUpload';
import { 
  CogIcon, 
  DocumentIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, type = 'warning' }) => {
  if (!isOpen) return null;

  const iconColor = type === 'danger' ? 'text-red-400' : 'text-yellow-400';
  const buttonColor = type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <ExclamationTriangleIcon className={`mx-auto mb-4 w-12 h-12 ${iconColor}`} />
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <div className="mt-2 px-7 py-3">
            <p className="text-sm text-gray-500">{message}</p>
          </div>
          <div className="flex justify-center space-x-4 px-4 py-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 ${buttonColor} text-white text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300`}
            >
              {type === 'danger' ? 'Delete' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ManagePolicies() {
  const [uploadResult, setUploadResult] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [limit, setLimit] = useState(20);
  
  // Multi-select states
  const [selectedPolicies, setSelectedPolicies] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPolicies, setExpandedPolicies] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', action: null, data: null });
  
  const queryClient = useQueryClient();

  // Fetch legal policies with search and pagination
  const { data: policiesData, isLoading: policiesLoading, error: policiesError } = useQuery({
    queryKey: ['legal-policies-search', searchQuery, selectedCategory, selectedSource, currentPage, limit],
    queryFn: () => searchLegalPolicies(searchQuery, selectedCategory, selectedSource, limit, currentPage * limit),
    keepPreviousData: true
  });

  // Fetch legal policy sources for filter dropdown
  const { data: sourcesData, isLoading: sourcesLoading, error: sourcesError } = useQuery({
    queryKey: ['legal-policy-sources'],
    queryFn: getLegalPolicySources
  });

  const policies = policiesData?.policies || [];
  const totalPolicies = policiesData?.total || 0;
  const hasMore = policiesData?.has_more || false;

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: uploadLegalPolicy,
    onMutate: () => {
      setUploadResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Uploading legal policy document...');
      
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 12;
        });
      }, 700);
      
      window.legalUploadProgressInterval = progressInterval;
      
      setTimeout(() => setLoadingMessage('Extracting privilege rules...'), 1200);
      setTimeout(() => setLoadingMessage('Parsing confidentiality guidelines...'), 2500);
      setTimeout(() => setLoadingMessage('Indexing for semantic search...'), 4000);
    },
    onSuccess: (data) => {
      if (window.legalUploadProgressInterval) {
        clearInterval(window.legalUploadProgressInterval);
        window.legalUploadProgressInterval = null;
      }
      
      setLoadingProgress(100);
      setTimeout(() => {
        setUploadResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
        queryClient.invalidateQueries({ queryKey: ['legal-policies-search'] });
        queryClient.invalidateQueries({ queryKey: ['legal-policy-sources'] });
      }, 2000);
    },
    onError: () => {
      if (window.legalUploadProgressInterval) {
        clearInterval(window.legalUploadProgressInterval);
        window.legalUploadProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  // Delete mutations
  const deleteSingleMutation = useMutation({
    mutationFn: deleteLegalPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-policies-search'] });
      queryClient.invalidateQueries({ queryKey: ['legal-policy-sources'] });
      setSelectedPolicies(new Set());
    }
  });

  const deleteBulkMutation = useMutation({
    mutationFn: deleteLegalPoliciesBulk,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-policies-search'] });
      queryClient.invalidateQueries({ queryKey: ['legal-policy-sources'] });
      setSelectedPolicies(new Set());
      setSelectAll(false);
    }
  });

  const deleteBySourceMutation = useMutation({
    mutationFn: deleteLegalPoliciesBySource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal-policies-search'] });
      queryClient.invalidateQueries({ queryKey: ['legal-policy-sources'] });
      setSelectedPolicies(new Set());
      setSelectAll(false);
    }
  });

  // Handle search changes
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(0);
    setSelectedPolicies(new Set());
    setSelectAll(false);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'category') {
      setSelectedCategory(value);
    } else if (filterType === 'source') {
      setSelectedSource(value);
    }
    setCurrentPage(0);
    setSelectedPolicies(new Set());
    setSelectAll(false);
  };

  // Handle policy expansion
  const togglePolicyExpansion = (policyId) => {
    const newExpanded = new Set(expandedPolicies);
    if (newExpanded.has(policyId)) {
      newExpanded.delete(policyId);
    } else {
      newExpanded.add(policyId);
    }
    setExpandedPolicies(newExpanded);
  };

  const expandAllPolicies = () => {
    const allPolicyIds = new Set(policies.map(policy => policy.policy_id));
    setExpandedPolicies(allPolicyIds);
  };

  const collapseAllPolicies = () => {
    setExpandedPolicies(new Set());
  };

  // Handle policy selection
  const handlePolicySelect = (policyId) => {
    const newSelected = new Set(selectedPolicies);
    if (newSelected.has(policyId)) {
      newSelected.delete(policyId);
    } else {
      newSelected.add(policyId);
    }
    setSelectedPolicies(newSelected);
    setSelectAll(newSelected.size === policies.length && policies.length > 0);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPolicies(new Set());
      setSelectAll(false);
    } else {
      const allPolicyIds = new Set(policies.map(policy => policy.policy_id));
      setSelectedPolicies(allPolicyIds);
      setSelectAll(true);
    }
  };

  // Handle delete actions
  const handleDeleteSingle = (policyId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteSingleMutation.mutate(policyId);
        setConfirmDialog({ isOpen: false });
      },
      data: { policyId },
      title: 'Delete Legal Policy',
      message: `Are you sure you want to delete policy "${policyId}"? This action cannot be undone.`
    });
  };

  const handleDeleteSelected = () => {
    const policyIds = Array.from(selectedPolicies);
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteBulkMutation.mutate(policyIds);
        setConfirmDialog({ isOpen: false });
      },
      data: { policyIds },
      title: 'Delete Selected Policies',
      message: `Are you sure you want to delete ${policyIds.length} selected policy(ies)? This action cannot be undone.`
    });
  };

  const handleDeleteBySource = (source) => {
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: () => {
        deleteBySourceMutation.mutate(source);
        setConfirmDialog({ isOpen: false });
      },
      data: { source },
      title: 'Delete All Policies from Source',
      message: `Are you sure you want to delete all policies from "${source}"? This action cannot be undone.`
    });
  };

  const handlePolicyUpload = (file) => {
    setUploadResult(null);
    uploadMutation.mutate(file);
  };

  // Get unique categories for filter
  const categories = [...new Set(policies.map(policy => policy.category))].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage Legal Policies</h1>
        <p className="mt-2 text-gray-600">
          Upload and manage legal policies, privilege rules, and confidentiality guidelines for eDiscovery
        </p>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div className="mb-8 bg-white border border-green-200 rounded-lg p-6 shadow-lg">
          <div className="flex items-center mb-3">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">📋 Legal Policy Successfully Processed!</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <span className="text-sm font-medium text-gray-700">File:</span>
              <p className="text-sm text-gray-900">{uploadResult.filename}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Extraction Method:</span>
              <p className="text-sm text-gray-900">{uploadResult.extraction_method}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Policies Found:</span>
              <p className="text-sm text-green-600 font-semibold">{uploadResult.policies_found}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Categories:</span>
              <p className="text-sm text-gray-900">{uploadResult.categories?.length || 0}</p>
            </div>
          </div>
          
          {uploadResult.policies_preview && uploadResult.policies_preview.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Policies Preview:</h4>
              <div className="space-y-2">
                {uploadResult.policies_preview.map((policy, index) => (
                  <div key={index} className="bg-gray-50 rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{policy.id}</span>
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {policy.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{policy.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Legal Policy */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Upload Legal Policy Document</h2>
          <p className="text-sm text-gray-600">
            Upload PDF, JSON, TXT, or Word (DOCX) files containing legal policies, privilege rules, 
            or confidentiality guidelines. The system will extract applicable rules for eDiscovery.
          </p>
        </div>
        
        <FileUpload 
          onFileSelect={handlePolicyUpload}
          accept=".pdf,.json,.txt,.docx,.doc"
          className="mb-4"
        />
        
        {uploadMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Policy upload failed: {uploadMutation.error?.response?.data?.detail || 'Unknown error'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Policy Management */}
      <div className="bg-white rounded-lg shadow">
        {/* Header with Search and Controls */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Legal Policies Management</h2>
            <div className="flex items-center space-x-2">
              <CogIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                {totalPolicies} policies total
              </span>
            </div>
          </div>

          {/* Search Bar and Controls */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search policies by ID, description, or keywords..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Per Page</label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUpIcon className="w-4 h-4 ml-2" /> : <ChevronDownIcon className="w-4 h-4 ml-2" />}
            </button>
          </div>

          {/* Filter Controls */}
          {showFilters && (
            <div className="flex items-center space-x-4 mb-4 p-4 bg-gray-50 rounded-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source File</label>
                <select
                  value={selectedSource}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                  disabled={sourcesLoading}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {sourcesLoading ? 'Loading sources...' : 
                     sourcesError ? 'Error loading sources' : 
                     'All Sources'}
                  </option>
                  {sourcesData?.sources?.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                  {!sourcesLoading && !sourcesError && (!sourcesData?.sources || sourcesData.sources.length === 0) && (
                    <option disabled>No sources available</option>
                  )}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSelectedCategory('');
                    setSelectedSource('');
                    setSearchQuery('');
                    setCurrentPage(0);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedPolicies.size > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
              <span className="text-sm text-blue-700">
                {selectedPolicies.size} policy(ies) selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDeleteSelected}
                  disabled={deleteBulkMutation.isLoading}
                  className="flex items-center px-3 py-1 text-sm text-red-700 hover:text-red-900 hover:bg-red-100 rounded"
                >
                  {deleteBulkMutation.isLoading ? (
                    <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <TrashIcon className="w-4 h-4 mr-1" />
                  )}
                  Delete Selected
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Policies List */}
        <div className="p-6">
          {policiesLoading ? (
            <div className="flex items-center justify-center py-8">
              <ClockIcon className="w-6 h-6 text-gray-400 animate-spin mr-2" />
              <span className="text-gray-600">Loading legal policies...</span>
            </div>
          ) : policiesError ? (
            <div className="text-center py-8">
              <ExclamationCircleIcon className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <p className="text-red-600 mb-2">Failed to load legal policies</p>
              <p className="text-sm text-gray-400">{policiesError.message}</p>
            </div>
          ) : policies.length > 0 ? (
            <div className="space-y-4">
              {/* Select All Header and View Controls */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Select All ({policies.length} policies)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={expandAllPolicies}
                    className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    Expand All
                  </button>
                  <button
                    onClick={collapseAllPolicies}
                    className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  >
                    <EyeSlashIcon className="w-4 h-4 mr-1" />
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Policies */}
              {policies.map((policy) => {
                const isExpanded = expandedPolicies.has(policy.policy_id);
                const hasExtendedInfo = policy.guidance || policy.examples || policy.scope || policy.exceptions;
                
                return (
                  <div key={policy.policy_id} className="border border-gray-200 rounded-lg hover:bg-gray-50">
                    {/* Main Policy Content */}
                    <div className="p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedPolicies.has(policy.policy_id)}
                          onChange={() => handlePolicySelect(policy.policy_id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <DocumentIcon className="w-5 h-5 text-gray-400" />
                              <span className="font-medium text-gray-900">{policy.policy_id}</span>
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                {policy.category}
                              </span>
                              {hasExtendedInfo && (
                                <button
                                  onClick={() => togglePolicyExpansion(policy.policy_id)}
                                  className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUpIcon className="w-4 h-4 mr-1" />
                                      Less Details
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDownIcon className="w-4 h-4 mr-1" />
                                      More Details
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {policy.source && (
                                <span className="text-xs text-gray-500">Source: {policy.source}</span>
                              )}
                              <button
                                onClick={() => handleDeleteSingle(policy.policy_id)}
                                disabled={deleteSingleMutation.isLoading}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete Policy"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Basic Information */}
                          {policy.description && (
                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">{policy.description}</p>
                          )}
                          
                          {/* Keywords */}
                          {policy.keywords && policy.keywords.length > 0 && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Keywords:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {policy.keywords.map((keyword, keywordIndex) => (
                                  <span
                                    key={keywordIndex}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Applicability */}
                          {policy.applicability && (
                            <div className="mb-3">
                              <span className="text-xs font-medium text-gray-700">Applicability:</span>
                              <p className="text-xs text-gray-600 mt-1">{policy.applicability}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Extended Information (Expandable) */}
                    {isExpanded && hasExtendedInfo && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Guidance */}
                          {policy.guidance && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                <InformationCircleIcon className="w-4 h-4 mr-1 text-blue-500" />
                                Guidance
                              </h4>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">{policy.guidance}</p>
                            </div>
                          )}

                          {/* Examples */}
                          {policy.examples && policy.examples.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Examples</h4>
                              <div className="bg-white p-3 rounded border">
                                {policy.examples.map((example, idx) => (
                                  <div key={idx} className="text-sm text-gray-700 mb-2 last:mb-0">
                                    <span className="text-xs text-gray-500">• </span>
                                    {example}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Scope */}
                          {policy.scope && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Scope</h4>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">{policy.scope}</p>
                            </div>
                          )}

                          {/* Exceptions */}
                          {policy.exceptions && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Exceptions</h4>
                              <p className="text-sm text-gray-700 bg-white p-3 rounded border">{policy.exceptions}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-gray-700">
                  Showing {currentPage * limit + 1} to {Math.min((currentPage + 1) * limit, totalPolicies)} of {totalPolicies} policies
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {currentPage + 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!hasMore}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CogIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">No legal policies found</p>
              <p className="text-sm text-gray-400">
                {searchQuery || selectedCategory || selectedSource 
                  ? 'Try adjusting your search criteria'
                  : 'Upload a legal policy document to begin defining privilege rules'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Source Management */}
      {sourcesData?.sources && sourcesData.sources.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Source File Management</h3>
          <div className="space-y-2">
            {sourcesData.sources.map(source => (
              <div key={source} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                <span className="text-sm text-gray-900">{source}</span>
                <button
                  onClick={() => handleDeleteBySource(source)}
                  disabled={deleteBySourceMutation.isLoading}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete All Policies
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">About Legal Policies</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            Legal policies define privilege rules, confidentiality guidelines, and data protection requirements 
            for eDiscovery processes. They contain specific guidance on handling attorney-client privilege, 
            work product doctrine, and other legal protections.
          </p>
          <p>
            <strong>Supported formats:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>PDF files:</strong> The system will extract text and identify privilege rules</li>
            <li><strong>JSON files:</strong> Structured format with predefined legal policy elements</li>
            <li><strong>TXT files:</strong> Plain text files with legal policies and guidelines</li>
            <li><strong>Word files (DOCX):</strong> Microsoft Word documents containing legal policies</li>
          </ul>
          <p>
            Use the search and filter tools to manage your legal policies efficiently. You can select multiple policies for bulk operations or delete entire source files.
          </p>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />
    </div>
  );
}